<template>
  <b-sidebar
    id="add-new-user-sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1 border-bottom">
        <div class="d-flex align-items-center">
          <feather-icon icon="UserPlusIcon" size="20" class="mr-50 text-primary" />
          <h5 class="mb-0 font-weight-bold">
            {{ dataId == null ? (typePath === 'staff' ? $t('Add Staff') : $t('Add Admin')) : $t('Update') }}
          </h5>
        </div>

        <feather-icon class="cursor-pointer text-muted" icon="XIcon" size="18" @click="hide" />
      </div>

      <!-- Form -->
      <validation-observer ref="formData">
        <b-form class="p-2">
          <b-row>
            <!-- General Info Section -->
            <b-col cols="12" class="mb-1">
              <span class="text-uppercase text-muted font-weight-bolder font-small-2">
                {{ $t('General Info') || 'ข้อมูลทั่วไป' }}
              </span>
            </b-col>

            <!-- Image Upload (Compact) -->
            <b-col cols="12" class="mb-1">
              <b-media no-body class="align-items-center">
                <b-media-aside>
                  <b-link>
                    <b-img v-if="image" ref="previewEl" rounded :src="image" height="64" width="64" style="object-fit: cover;" />
                    <b-img v-else ref="previewEl" :src="require('@/assets/images/blank/no_image.png')" rounded height="64" width="64" />
                  </b-link>
                </b-media-aside>

                <b-media-body class="ml-1">
                  <b-button v-ripple.400="'rgba(255, 255, 255, 0.15)'" variant="outline-primary" size="sm" class="mr-50" @click="$refs.refInputEl.$el.click()">
                    <feather-icon icon="UploadIcon" size="14" class="mr-25" />
                    {{ $t("Upload") }}
                  </b-button>
                  <b-button v-ripple.400="'rgba(186, 191, 199, 0.15)'" variant="outline-secondary" size="sm" v-if="image" @click="resetImage">
                    {{ $t("Reset") }}
                  </b-button>
                  <b-form-file ref="refInputEl" accept=".webp, .jpeg, .jpg, .png, .gif" :hidden="true" plain @change="inputImageRenderer" />
                </b-media-body>
              </b-media>
            </b-col>

            <!-- firstName & lastName side by side -->
            <b-col cols="6">
              <b-form-group :label="$t('FirstName')" label-for="register-firstName">
                <validation-provider #default="{ errors }" name="FirstName" vid="firstName" rules="required">
                  <b-form-input
                    id="register-firstName"
                    v-model="firstName"
                    name="register-firstName"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="$t('FirstName')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="6">
              <b-form-group :label="$t('LastName')" label-for="register-lastName">
                <validation-provider #default="{ errors }" name="LastName" vid="lastName" rules="required">
                  <b-form-input
                    id="register-lastName"
                    v-model="lastName"
                    name="register-lastName"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="$t('LastName')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- phoneNumber & username side by side -->
            <b-col cols="6">
              <b-form-group :label="$t('PhoneNumber')" label-for="register-phoneNumber">
                <validation-provider #default="{ errors }" name="PhoneNumber" vid="phoneNumber" rules="required">
                  <b-form-input
                    id="register-phoneNumber"
                    v-model="tel"
                    name="register-phoneNumber"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="$t('PhoneNumber')"
                    @input="onPhoneInput"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="6">
              <b-form-group :label="$t('Username')" label-for="register-username">
                <validation-provider #default="{ errors }" name="Username" vid="username" rules="required">
                  <b-form-input
                    id="register-username"
                    v-model="username"
                    :disabled="dataId !== null"
                    name="register-username"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="$t('Username')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- Password Section (when creating) -->
            <b-col v-if="dataId == null" cols="12">
              <b-form-group label-for="register-password">
                <template #label>
                  <div class="d-flex justify-content-between align-items-center">
                    <span>{{ $t('Password') }}</span>
                    <b-link class="font-small-2 text-primary" @click="generatePassword">
                      <feather-icon icon="KeyIcon" size="12" class="mr-25" />
                      {{ $t('Auto Generate') }}
                    </b-link>
                  </div>
                </template>
                <validation-provider #default="{ errors }" name="Password" vid="password" :rules="dataId == null ? 'required' : ''">
                  <b-input-group class="input-group-merge" :class="errors.length > 0 ? 'is-invalid' : null">
                    <b-form-input
                      id="register-password"
                      v-model="password"
                      class="form-control-merge"
                      :type="passwordFieldType"
                      :state="errors.length > 0 ? false : null"
                      name="register-password"
                      :placeholder="$t('Password')"
                    />
                    <b-input-group-append is-text>
                      <feather-icon :icon="passwordToggleIcon" class="cursor-pointer" @click="togglePasswordVisibility" />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- Role & Permission Section -->
            <b-col cols="12" class="mt-1">
              <div class="d-flex align-items-center mb-50">
                <feather-icon icon="ShieldIcon" size="16" class="mr-50 text-primary" />
                <span class="font-weight-bold text-dark">{{ $t('Select Role / Permission') }}</span>
              </div>

              <!-- Quick Select Badges -->
              <div v-if="respDataPermission && respDataPermission.length > 0" class="d-flex flex-wrap mb-1">
                <b-badge
                  v-for="perm in respDataPermission"
                  :key="perm._id"
                  :variant="permissionInAppId === perm._id ? 'primary' : 'light-secondary'"
                  class="cursor-pointer p-50 mr-50 mb-50"
                  style="font-size: 0.85rem;"
                  @click="permissionInAppId = perm._id"
                >
                  <feather-icon :icon="permissionInAppId === perm._id ? 'CheckCircleIcon' : 'CircleIcon'" size="14" class="mr-25" />
                  {{ perm.name }}
                </b-badge>
              </div>

              <b-form-group label-for="register-permissionInAppId">
                <validation-provider #default="{ errors }" name="permissionInAppId" vid="permissionInAppId" rules="required">
                  <v-select
                    id="register-permissionInAppId"
                    v-model="permissionInAppId"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    label="name"
                    :options="respDataPermission"
                    :reduce="perm => perm._id"
                    class="select-size-lg"
                    :placeholder="$t('Select Role / Permission')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Action buttons -->
          <div class="d-flex mt-2 pt-1 border-top">
            <b-button v-ripple.400="'rgba(255, 255, 255, 0.15)'" variant="primary" class="mr-1 flex-grow-1" @click="validationForm()">
              <feather-icon icon="CheckIcon" class="mr-50" />
              {{ $t('Submit') }}
            </b-button>
            <b-button type="button" variant="outline-secondary" @click="hide">
              {{ $t('Cancel') }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BSidebar,
  BButton,
  BFormFile,
  BMedia,
  BMediaAside,
  BMediaBody,
  BLink,
  BImg,
  BBadge,
  BInputGroupAppend,
  BInputGroup,
  BFormInput,
  BForm,
  BFormGroup,
  BRow,
  BCol,
} from 'bootstrap-vue'
import { required } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import vSelect from 'vue-select'
import store from '@/store'

export default {
  components: {
    BSidebar,
    BButton,
    BFormFile,
    BMedia,
    BMediaAside,
    BMediaBody,
    BLink,
    BImg,
    BBadge,
    BForm,
    BFormInput,
    BFormGroup,
    BInputGroupAppend,
    BInputGroup,
    ValidationProvider,
    ValidationObserver,
    BRow,
    BCol,
    vSelect,
  },
  directives: {
    Ripple,
  },
  mixins: [togglePasswordVisibility],
  model: {
    prop: 'isSidebarActive',
    event: 'update:is-sidebar-active',
  },
  props: {
    typePath: {
      type: String,
      required: true,
    },
    storeModuleName: {
      type: String,
      required: true,
    },
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    show: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      dataId: null,
      image: '',
      firstName: '',
      lastName: '',
      username: '',
      password: '',
      tel: '',
      role: '',
      permissionInAppId: '',
      required,
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    respDataPermission() {
      return store.state.permissionInApp.respData != null
        ? store.state.permissionInApp.respData.data
        : []
    },
  },
  watch: {
    isSidebarActive(val) {
      if (val) {
        if (Object.entries(this.data).length === 0) {
          this.$refs.formData.reset()
          this.initValues()
        } else {
          const {
            image, firstName, lastName, userId, tel, permissionInAppId,
          } = this.data
          this.dataId = userId._id
          this.image = image
          this.firstName = firstName
          this.lastName = lastName
          this.username = userId.username
          this.tel = tel
          this.role = this.typePath
          if (permissionInAppId !== null && permissionInAppId !== undefined) {
            this.permissionInAppId = permissionInAppId
          } else {
            this.permissionInAppId = ''
          }
        }
      }
    },
  },
  methods: {
    onPhoneInput(val) {
      if (this.dataId == null && !this.username) {
        this.username = val
      }
    },
    generatePassword() {
      const chars = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789'
      let pass = ''
      for (let i = 0; i < 8; i += 1) {
        pass += chars.charAt(Math.floor(Math.random() * chars.length))
      }
      this.password = pass
      this.passwordFieldType = 'text'
      this.$toast({
        component: ToastificationContent,
        position: 'bottom-right',
        props: {
          title: this.$t('Auto Generate'),
          icon: 'KeyIcon',
          variant: 'info',
          text: `${this.$t('Password')}: ${pass}`,
        },
      })
    },
    inputImageRenderer(input) {
      if (input.target.files && input.target.files[0]) {
        if (input.target.files[0].size > 700000) {
          this.$toast({
            component: ToastificationContent,
            position: 'bottom-right',
            props: {
              title: this.$t('Error'),
              icon: 'ErrorIcon',
              variant: 'danger',
              text: `${this.$t('Allowed')} JPG, GIF ${this.$t('or')} PNG. ${this.$t('Max size of')} 700KB`,
            },
          })
          return
        }

        const reader = new FileReader()
        reader.onload = e => {
          this.image = e.target.result
        }
        reader.readAsDataURL(input.target.files[0])
      }
    },
    resetImage() {
      this.image = ''
    },
    initValues() {
      this.dataId = null
      this.image = ''
      this.firstName = ''
      this.lastName = ''
      this.username = ''
      this.password = ''
      this.tel = ''
      this.role = ''
      this.permissionInAppId = ''
    },
    validationForm() {
      this.$refs.formData.validate().then(success => {
        if (success) {
          const isNew = this.dataId == null
          const createdUsername = this.username

          const obj = {
            userId: this.dataId,
            image: this.image,
            firstName: this.firstName,
            lastName: this.lastName,
            username: this.username,
            password: this.password,
            tel: this.tel,
            role: this.typePath,
            permissionInAppId: this.permissionInAppId,
          }
          this.$emit('update:show', true)
          if (!isNew) {
            store
              .dispatch(`${this.storeModuleName}/update`, obj)
              .then(() => {
                this.$emit('update:is-sidebar-active', false)
                this.$emit('update:show', false)

                this.$toast({
                  component: ToastificationContent,
                  position: 'bottom-right',
                  props: {
                    title: this.$t('Success') || 'สำเร็จ',
                    icon: 'CheckIcon',
                    variant: 'success',
                    text: this.$t('User updated successfully'),
                  },
                })
              })
              .catch(error => {
                console.log('fetchUsers Error : ', error)
                this.$toast({
                  component: ToastificationContent,
                  position: 'bottom-right',
                  props: {
                    title: this.$t('Error'),
                    icon: 'ErrorIcon',
                    variant: 'danger',
                    text: this.$t(error.response?.data?.message || 'Update failed'),
                  },
                })
                this.$emit('update:show', false)
              })
          } else {
            delete obj.userId
            store
              .dispatch(`${this.storeModuleName}/add`, obj)
              .then(() => {
                this.$emit('update:is-sidebar-active', false)
                this.$emit('update:show', false)

                this.$toast({
                  component: ToastificationContent,
                  position: 'bottom-right',
                  props: {
                    title: this.$t('Success') || 'สำเร็จ',
                    icon: 'CheckIcon',
                    variant: 'success',
                    text: `${this.$t('User added successfully')} | ${this.$t('Username')}: ${createdUsername}`,
                  },
                })
              })
              .catch(error => {
                console.log('fetchUsers Error : ', error)
                this.$toast({
                  component: ToastificationContent,
                  position: 'bottom-right',
                  props: {
                    title: this.$t('Error'),
                    icon: 'ErrorIcon',
                    variant: 'danger',
                    text: this.$t(error.response?.data?.message || 'Add failed'),
                  },
                })
                this.$emit('update:show', false)
              })
          }
        }
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

#add-new-user-sidebar {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}
</style>
